import { ReactElement, useCallback, useEffect, useMemo, useRef } from 'react';
import { TFunction } from 'i18next';
import { useUITranslation } from '../../hooks';
import styles from './ui-filter.module.scss';
import { UIPopover, UIPopoverPlacement, UIPropoverRef } from '../ui-popover';
import { UIMenu, UIMenuItem } from '../ui-menu';
import { UIFilterHeader } from './ui-filter-header';
import { PeriodFilterEnum } from '.';
import { blue } from '../../styles';

type UIPeriodFilterItemProps = {
    periodType: PeriodFilterEnum;
    popoverPlacement?: UIPopoverPlacement;
    onPeriodChange: (type: PeriodFilterEnum) => void;
    options: PeriodFilterEnum[];
};

export function UIPeriodFilterItem(
    props: UIPeriodFilterItemProps
): ReactElement {
    const { t } = useUITranslation();
    const { periodType, popoverPlacement, onPeriodChange, options } = props;
    const ref = useRef<UIPropoverRef>(null);

    function onClickOutside() {
        ref.current?.close();
    }

    const onPeriodChangeRef = useRef(onPeriodChange);
    useEffect(() => {
        onPeriodChangeRef.current = onPeriodChange;
    });

    const onItemPress = useCallback((type: PeriodFilterEnum) => {
        ref.current?.close();
        onPeriodChangeRef.current?.(type);
    }, []);

    const text = useMemo(() => {
        return mapPeriodType(periodType, t);
    }, [periodType, t]);

    const items = useMemo(() => {
        return options.map((option) => ({
            type: option,
            onClick: () => onItemPress(option)
        }));
    }, [options, onItemPress]);

    return (
        <UIPopover
            ref={ref}
            popoverPadding={8}
            width={175}
            placement={popoverPlacement}
            triggerNode={
                <button className={styles.buttonContainer}>
                    <UIFilterHeader
                        text={text}
                        // icon={
                        //     <UIIcon
                        //         name="calendar"
                        //         size={18}
                        //         color={charcoal}
                        //     />
                        // }
                    />
                </button>
            }
            onClickOutside={onClickOutside}
        >
            <UIMenu scroll>
                {items.map((item, index) => (
                    <UIMenuItem
                        key={index}
                        text={mapPeriodType(item.type, t)}
                        onClick={item.onClick}
                        leftIconName={
                            item.type === periodType ? 'check' : undefined
                        }
                        leftIconColor={blue}
                    />
                ))}
            </UIMenu>
        </UIPopover>
    );
}

function mapPeriodType(timeType: PeriodFilterEnum, t: TFunction): string {
    switch (timeType) {
        case PeriodFilterEnum.Daily:
            return t('components.ui_period_filter_item.daily');
        case PeriodFilterEnum.Weekly:
            return t('components.ui_period_filter_item.weekly');
        case PeriodFilterEnum.Monthly:
            return t('components.ui_period_filter_item.monthly');
    }
}
